import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { toast } from "@/hooks/use-toast";
import { PlayCircle, Eye, EyeOff } from "lucide-react";
import { resetPasswordService } from "@/services/resetPassword";

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();

  // Form states
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState<null | string>(null);
  const [isPasswordMatched, setIsPasswordMatched] = useState<boolean | null>(null);
  
  const [isLoading, setIsLoading] = useState(false);

  // Check if this is token-based reset (new password entry)
  const isTokenReset = !!token;

  const handleEmailChange = (value: string) => {
    setEmail(value);
    setEmailError("");
  };

  const handlePasswordChange = (value: string) => {
    setPassword(value);
    checkPasswords(value, confirmPassword);
  };

  const handleConfirmPasswordChange = (value: string) => {
    setConfirmPassword(value);
    checkPasswords(password, value);
  };

  const checkPasswords = (pwd: string, confirmPwd: string) => {
    if (!confirmPwd) {
      setIsPasswordMatched(null);
      setPasswordMessage(null);
      return;
    }

    if (pwd === confirmPwd) {
      setIsPasswordMatched(true);
      setPasswordMessage("Passwords matched!");
    } else {
      setIsPasswordMatched(false);
      setPasswordMessage("The password is not the same!");
    }
  };

  // Handle request reset password (no token)
  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    // Validate email
    const emailPattern = /^[^\s@]+@[^\s@]+\.com$/;
    if (!emailPattern.test(email)) {
      setEmailError("Please enter a valid email format");
      setIsLoading(false);
      return;
    }

    try {
      const response = await resetPasswordService.requestResetPassword(email);
      console.log(response)
      if (response.success === true) {
        toast({
          title: "Reset Link Sent",
          description: "Please check your email for the reset link.",
        });
      } else {
        toast({
          title: "Request Failed",
          description: response.message || "Something went wrong.",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle password reset with token
  const handleTokenReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(false);

    if (password !== confirmPassword) {
      toast({
        title: "Password mismatch",
        description: "Passwords do not match.",
        variant: "destructive",
      });
      return;
    }

    if (password.length < 3) {
      toast({
        title: "Invalid Password",
        description: "Password must be at least 3 characters.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const response = await resetPasswordService.resetPassword(token!, password);
      
      if (response.success === true) {
        toast({
          title: "Password Reset Successful",
          description: "You can now login with your new password.",
        });
        navigate("/login");
      } else {
        toast({
          title: "Reset Failed",
          description: response.message || "Something went wrong.",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center px-4">
      {/* BACKGROUND */}
      <div className="absolute inset-0 bg-gradient-to-br from-green-600 via-emerald-500 to-lime-400" />
      <div className="absolute inset-0 bg-black/30" />

      {/* CONTENT */}
      <div className="relative w-full max-w-md">
        {/* BRAND */}
        <div className="text-center mb-10">
          <div className="flex items-center justify-center gap-3 mb-3">
            <PlayCircle className="w-10 h-10 text-white drop-shadow" />
            <h1 className="text-3xl font-bold text-white tracking-tight">
              Court Replay
            </h1>
          </div>
          <p className="text-white/80 text-sm">
            {isTokenReset 
              ? "Create your new password" 
              : "Enter your email to reset your password"}
          </p>
        </div>

        {/* CARD */}
        <Card className="border-0 bg-white/90 backdrop-blur-xl shadow-2xl rounded-2xl">
          <CardHeader className="text-center space-y-1">
            <CardTitle className="text-2xl">
              {isTokenReset ? "New Password" : "Reset Password"}
            </CardTitle>
            <CardDescription>
              {isTokenReset 
                ? "Enter your new password below" 
                : "We'll send you a link to reset your password"}
            </CardDescription>
          </CardHeader>

          <CardContent>
            <form 
              onSubmit={isTokenReset ? handleTokenReset : handleRequestReset} 
              className="space-y-5"
            >
              {isTokenReset ? (
                <>
                  {/* NEW PASSWORD */}
                  <div className="space-y-1.5">
                    <Label htmlFor="password">New Password</Label>
                    <p className="text-xs text-muted-foreground">
                      Password must be at least 3 characters
                    </p>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter new password"
                        value={password}
                        onChange={(e) => handlePasswordChange(e.target.value)}
                        className="h-11 rounded-xl pr-10"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>

                  {/* CONFIRM PASSWORD */}
                  <div className="space-y-1.5">
                    <Label htmlFor="confirmPassword">Confirm Password</Label>

                    {passwordMessage && (
                      <p
                        className={`text-sm font-medium ${
                          isPasswordMatched ? "text-green-600" : "text-red-600"
                        }`}
                      >
                        {passwordMessage}
                      </p>
                    )}

                    <div className="relative">
                      <Input
                        id="confirmPassword"
                        type={showPassword ? "text" : "password"}
                        placeholder="Re-enter password"
                        value={confirmPassword}
                        onChange={(e) => handleConfirmPasswordChange(e.target.value)}
                        className={`h-11 rounded-xl pr-10 ${
                          isPasswordMatched === null
                            ? ""
                            : isPasswordMatched
                            ? "border-green-500 focus:border-green-600"
                            : "border-red-500 focus:border-red-600"
                        }`}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                /* EMAIL (no token) */
                <div className="space-y-1.5">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => handleEmailChange(e.target.value)}
                    className="h-11 rounded-xl"
                    required
                  />
                  {emailError && (
                    <p className="text-sm text-red-500">{emailError}</p>
                  )}
                </div>
              )}

              {/* SUBMIT */}
              <Button
                type="submit"
                className="w-full h-11 rounded-xl text-base"
                disabled={isLoading}
              >
                {isLoading 
                  ? (isTokenReset ? "Resetting..." : "Sending...") 
                  : (isTokenReset ? "Reset Password" : "Send Reset Link")}
              </Button>
            </form>

            {/* FOOTER */}
            <div className="mt-6 text-center text-sm">
              <span className="text-muted-foreground">
                Remember your password?
              </span>{" "}
              <Link
                to="/login"
                className="font-medium text-primary hover:underline"
              >
                Sign in
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ResetPassword;
